{
  if (!window.customElements.get('collection-merchandising')) {
    const galleryBucket = (n) => {
      if (n <= 1) return '1';
      if (n === 2) return '2';
      if (n === 3) return '3';
      return '4plus';
    };

    const normalize = (str) =>
      (str ?? '')
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const parseKeywords = (raw) =>
      normalize(raw)
        .split(/[,|]/)
        .map((s) => s.trim())
        .filter(Boolean);

    class CollectionMerchandising extends HTMLElement {
      #rendered = null;
      #productList = null;

      connectedCallback() {
        const target = this.getAttribute('target');
        if (!target) return;

        const productList = document.getElementById(target);
        if (!productList) return;
        this.#productList = productList;

        const templates = [...this.querySelectorAll('template[data-cm-block]')];
        if (templates.length === 0) return;

        const blocks = templates
          .map((t) => ({
            order: Math.max(1, parseInt(t.dataset.order, 10) || 999),
            keywords: parseKeywords(t.dataset.keyword),
            galleryCount: parseInt(t.dataset.galleryCount, 10) || 0,
            featureCount: parseInt(t.dataset.featureCount, 10) || 0,
            featureHandles: [t.dataset.featureHandle, t.dataset.featureHandle2]
              .map((h) => (h ?? '').trim().toLowerCase())
              .filter(Boolean),
            template: t,
          }))
          .sort((a, b) => a.order - b.order);

        const claimed = new Set();
        const perBlockProducts = new Map();
        const cards = [...productList.querySelectorAll('product-card')];
        const cardsToRemove = new Set();

        for (const block of blocks) {
          if (block.keywords.length === 0) {
            perBlockProducts.set(block, []);
            continue;
          }

          const tentativelyClaimed = [];
          for (const handle of block.featureHandles) {
            if (!claimed.has(handle)) {
              claimed.add(handle);
              tentativelyClaimed.push(handle);
            }
          }

          const matches = cards.filter((card) => {
            const handle = card.getAttribute('handle');
            if (handle && claimed.has(handle)) return false;
            const title = normalize(card.querySelector('.product-title')?.textContent);
            return block.keywords.some((kw) => title.includes(kw));
          });

          if (matches.length === 0) {
            for (const handle of tentativelyClaimed) claimed.delete(handle);
            perBlockProducts.set(block, []);
            continue;
          }

          for (const handle of block.featureHandles) {
            const dup = cards.find((c) => c.getAttribute('handle')?.toLowerCase() === handle);
            if (dup) cardsToRemove.add(dup);
          }
          for (const card of matches) {
            const handle = card.getAttribute('handle');
            if (handle) claimed.add(handle);
          }
          perBlockProducts.set(block, matches);
        }

        for (const card of cardsToRemove) card.remove();

        const activeBlocks = blocks.filter((b) => perBlockProducts.get(b).length > 0);
        if (activeBlocks.length === 0) return;

        this.#rendered = activeBlocks.map((block) => {
          const el = block.template.content.firstElementChild.cloneNode(true);
          el.classList.add(`cm-block--gallery-${galleryBucket(block.galleryCount)}`);
          el.classList.add(`cm-block--features-${block.featureCount}`);
          el.setAttribute('data-cm-active', '');
          const slot = el.querySelector('[data-cm-products]');
          if (slot) {
            const products = perBlockProducts.get(block);
            for (const [index, card] of products.entries()) {
              const last = index === products.length - 1;
              const mobileSpan = last && products.length % 2 === 1 ? 2 : 1;
              const desktopSpan = last ? 3 - ((products.length - 1) % 3) : 1;
              const imageSizes = `(max-width: 699px) calc((100vw - 32px) * ${mobileSpan} / 2), (max-width: 999px) calc((100vw - 64px) * ${desktopSpan} / 3), calc(min(80vw, 1280px) * ${desktopSpan} / 3)`;
              for (const image of card.querySelectorAll('.product-card__image')) {
                image.sizes = imageSizes;
              }
              slot.appendChild(card);
            }
          }
          if (block.galleryCount >= 4 && block.featureCount === 1) {
            for (const image of el.querySelectorAll('.cm-block__gallery-cell--product .product-card__image')) {
              image.sizes = '(max-width: 999px) 100vw, calc(min(80vw, 1280px) * 2 / 3)';
            }
          }
          return { block, el };
        });

        this.#position();
      }

      #position() {
        if (!this.#rendered || !this.#productList) return;
        for (const { el } of this.#rendered) el.remove();

        const productList = this.#productList;

        for (const { block, el } of this.#rendered) {
          const cards = [...productList.querySelectorAll(':scope > product-card')];
          const anchor = cards[Math.min(block.order - 1, cards.length)];
          if (anchor) {
            productList.insertBefore(el, anchor);
          } else {
            productList.appendChild(el);
          }
        }
      }

      disconnectedCallback() {
        this.#rendered = null;
        this.#productList = null;
      }
    }

    window.customElements.define('collection-merchandising', CollectionMerchandising);
  }
}
